/**
 * Forgot-password screen — one email field, POST /auth/password/reset/request.
 *
 * THE NO-ENUMERATION CONTRACT IS THE WHOLE POINT OF THIS SCREEN and it is
 * unchanged (ForgotPasswordScreen.test.tsx asserts all three branches):
 *   - On submit the screen ALWAYS shows the same neutral notice, whether the
 *     address exists, does not exist, or the server 500s. Anything that varied
 *     between those branches — wording, icon, timing, ARIA role — would let an
 *     attacker enumerate accounts, so the notice is rendered from one code path
 *     with one string.
 *   - The ONE thing we do differentiate is a hard network failure, because the
 *     user genuinely needs to know their request never left the phone. Even
 *     then the neutral sentence is shown verbatim first, and the network note
 *     is appended after it.
 *
 * ── The counter rebuild (2026-08) ────────────────────────────────────────────
 *
 * A single-field form used to render as a lone boxed input floating in a column
 * of whitespace, which made a one-step screen look unfinished. It is now the
 * same ruled slip as every other auth screen — one row, drawn edges, the same
 * caps label — so the flow reads as one document being filled in across four
 * pages rather than four unrelated pages.
 *
 * The neutral notice is a recessed panel with a mail icon: a receipt for an
 * action that has been taken, not an alert about something that went wrong.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { MailCheck } from "lucide-react";
import { useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Stagger, StaggerItem } from "@/components/motion";
import { AuthCounter } from "@/features/auth/components/AuthCounter";
import { AuthLink, AuthSubmit } from "@/features/auth/components/controls";
import {
  SLIP_INPUT_CLASS,
  Slip,
  SlipField,
  SlipNotice,
} from "@/features/auth/components/slip";
import { withReturnTo } from "@/features/auth/destination";
import {
  forgotPasswordDefaultValues,
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
} from "@/features/auth/schemas";
import { NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { iconProps } from "@/lib/icons";
import { useUiStore } from "@/lib/ui-store";

interface PasswordResetRequestResponse {
  message_key: "auth.password.reset.email_sent_if_exists";
}

export function ForgotPasswordScreen(): ReactElement {
  const { t } = useTranslation("auth");
  const uiLanguage = useUiStore((s) => s.language);
  const [searchParams] = useSearchParams();
  const rawReturnTo = searchParams.get("return_to");
  const [neutralShown, setNeutralShown] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: forgotPasswordDefaultValues,
    mode: "onBlur",
  });

  const onSubmit = async (values: ForgotPasswordFormValues): Promise<void> => {
    setNetworkError(false);
    try {
      // The backend's PasswordResetRequest accepts `language: 'ru' | 'ky'`.
      // EN was added to UI scope per ADR-028 but the backend hasn't been
      // amended yet — pass through 'ru' on the wire for EN until the server
      // schema catches up. The user-facing message is identical in all
      // branches either way.
      const wireLanguage = uiLanguage === "en" ? "ru" : uiLanguage;
      await apiClient.post<
        PasswordResetRequestResponse,
        { email: string; language: "ru" | "ky" }
      >(
        AUTH_PATHS.passwordResetRequest,
        { email: values.email, language: wireLanguage },
        { skipRefreshOn401: true },
      );
    } catch (err) {
      // Per REQ-PRD-§11.1: the response is always the same neutral notice.
      // The ONLY exception we surface is a hard network failure (the user
      // might want to retry); even then, we never say whether the email
      // matched. An ApiError (e.g. a 422 or a 500) falls through silently.
      if (err instanceof NetworkError) {
        setNetworkError(true);
      }
    } finally {
      setNeutralShown(true);
    }
  };

  const submit = form.handleSubmit(onSubmit);
  const isSubmitting = form.formState.isSubmitting;
  const emailErrorKey = form.formState.errors.email?.message;

  return (
    <AuthCounter
      door="consumer"
      doorName={t("forgotPassword.doorName")}
      title={t("forgotPassword.title")}
      lede={t("forgotPassword.lede")}
      testId="forgot-password-screen"
    >
      <Stagger className="gav2-stack-lg flex flex-col">
        <StaggerItem>
          <form className="gav2-stack-md flex flex-col" onSubmit={submit} noValidate>
            <Slip>
              <SlipField
                htmlFor="forgot-email"
                label={t("forgotPassword.emailLabel")}
                required
                requiredLabel={t("common.requiredField")}
                error={emailErrorKey !== undefined ? t(emailErrorKey) : undefined}
                errorId="forgot-email-error"
                errorTestId="forgot-email-error"
              >
                <input
                  id="forgot-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  aria-invalid={emailErrorKey !== undefined || undefined}
                  aria-describedby={
                    emailErrorKey !== undefined ? "forgot-email-error" : undefined
                  }
                  placeholder={t("forgotPassword.emailPlaceholder")}
                  disabled={isSubmitting}
                  data-testid="forgot-email"
                  className={SLIP_INPUT_CLASS}
                  {...form.register("email")}
                />
              </SlipField>
            </Slip>

            <AuthSubmit
              pending={isSubmitting}
              pendingLabel={t("forgotPassword.submitting")}
              testId="forgot-submit"
            >
              {t("forgotPassword.submit")}
            </AuthSubmit>

            {neutralShown ? (
              <SlipNotice
                tone="neutral"
                role="status"
                icon={<MailCheck {...iconProps("sm")} />}
                testId="forgot-neutral-message"
              >
                {t("forgotPassword.neutralMessage")}
                {networkError ? (
                  <span className="ml-1 text-[var(--g-ink-2)]">
                    {t("errors.network")}
                  </span>
                ) : null}
              </SlipNotice>
            ) : null}
          </form>
        </StaggerItem>

        <StaggerItem className="flex flex-col items-start gap-1">
          <AuthLink
            to={withReturnTo("/reset-password", rawReturnTo)}
            testId="forgot-have-code-link"
          >
            {t("forgotPassword.haveCodeLink")}
          </AuthLink>
          <AuthLink
            to={withReturnTo("/login", rawReturnTo)}
            testId="forgot-back-link"
            tone="muted"
          >
            {t("forgotPassword.backToLogin")}
          </AuthLink>
        </StaggerItem>
      </Stagger>
    </AuthCounter>
  );
}

export default ForgotPasswordScreen;
